import { readFileSync } from 'node:fs'
import { api, formatError } from '../api-client'
import { analyticsEvent, flushAndExit } from '../analytics'
import { parseArgs } from './context/args'
import { emitJSON } from './context/render'
import { checkSecretsOrExit, isAllowSecrets } from './context/validation'
import { levenshtein } from './context/flags'
import { resolveWorkspaceSlug } from './context'
// One canonical note-slug derivation, shared with the MCP (the suffix is
// injectable for deterministic tests).
import { deriveNoteSlug } from '@driftless/topic-schema'
export { deriveNoteSlug }

// Where a saved note lives in the dashboard — used to build the `url` in --json.
const DASHBOARD_URL = process.env.DRIFTLESS_DASHBOARD_URL ?? 'https://app.driftless.icu'

// A note is ALWAYS a private draft. We send no status and no is_private — the
// API defaults (status='draft', is_private=true) make it so. Zero contract
// change. The content goes in `what` (a short field, ≤8KB, secret-scanned).
export function buildNotePayload(content: string, suffix?: string): { name: string; what: string } {
  return { name: deriveNoteSlug(content, { suffix }), what: content.trim() }
}

const KNOWN_NOTE_FLAGS: ReadonlySet<string> = new Set([
  'content', 'project', 'card', 'suggest-topic', 'allow-secrets', 'json', 'help',
])

// cli-core convention: unknown flags ALWAYS error with a Levenshtein suggestion,
// never silently ignored.
export function assertKnownNoteFlags(
  flags: Record<string, string | boolean>,
  onError: (msg: string) => never = (msg) => { console.error(msg); process.exit(1) },
): void {
  const unknown = Object.keys(flags).filter((f) => !KNOWN_NOTE_FLAGS.has(f))
  if (unknown.length === 0) return
  const lines = unknown.map((f) => {
    let best: string | null = null
    let bestDist = Infinity
    for (const known of KNOWN_NOTE_FLAGS) {
      const d = levenshtein(f, known)
      if (d < bestDist) { bestDist = d; best = known }
    }
    const threshold = Math.max(2, Math.floor(f.length / 2))
    const hint = best !== null && bestDist <= threshold ? best : null
    return `  --${f}${hint ? `  (did you mean --${hint}?)` : ''}`
  })
  onError(
    `Error: unknown flag${unknown.length > 1 ? 's' : ''} for 'driftless note':\n` +
      lines.join('\n') +
      `\nRun 'driftless note add --help' for valid flags.`,
  )
}

// Single source of truth for `note` help lives in ./help-text (a light,
// dep-free module) so index.ts can print `note --help` without importing this
// heavy command body. Re-exported here so callers (and the help-consistency
// spec) still resolve NOTE_HELP from './note'.
export { NOTE_HELP } from './help-text'
import { NOTE_HELP } from './help-text'

function printNoteHelp(stream: NodeJS.WritableStream = process.stdout): void {
  stream.write(NOTE_HELP)
}

export async function noteCommand(args: string[]): Promise<void> {
  const { flags, positional } = parseArgs(args)
  const sub = positional[0]
  const isJSON = !!flags['json']

  // Help is local-only — no network, no auth.
  if (flags['help'] === true || sub === 'help') {
    printNoteHelp()
    process.exit(0)
  }
  if (sub === undefined) {
    printNoteHelp(process.stderr)
    process.exit(1)
  }
  if (sub !== 'add') {
    console.error(`Unknown note subcommand: '${sub}'. Did you mean "note add"?`)
    process.exit(1)
  }

  assertKnownNoteFlags(flags)

  // content is required; @path reads from a file (mirrors `context add --content`).
  let content = flags['content'] as string | undefined
  if (typeof content === 'string' && content.startsWith('@')) {
    const path = content.slice(1)
    try { content = readFileSync(path, 'utf-8') } catch { console.error(`File not found: ${path}`); process.exit(1) }
  }
  if (!content || !content.trim()) {
    console.error('Error: --content is required.')
    printNoteHelp(process.stderr)
    process.exit(1)
  }

  // --card is nested under a project; require --project so we never create an
  // orphan note we then can't attach.
  if (flags['card'] && !flags['project']) {
    console.error('Error: --card requires --project (the card lives inside a project).')
    process.exit(1)
  }

  const workspaceSlug = await resolveWorkspaceSlug()
  const body = buildNotePayload(content)

  // Secret hygiene: notes go through the same local scan as topics. The scan
  // reads the exact body we POST, so `what` (the note content) is covered.
  const allowSecrets = isAllowSecrets(flags)
  checkSecretsOrExit(body, allowSecrets)

  // --card needs --project: the card route is nested under its project.
  // Both ride the SAME create call now (server-side, one transaction) — no
  // second PATCH to attach the note after the fact. That second call used to
  // be able to fail invisibly (wrong id, timing, access) while the note above
  // still saved fine, leaving an orphan that looked like a personal note
  // forever. One call means one outcome: either the note AND its origin are
  // saved together, or neither is.
  const projectId = typeof flags['project'] === 'string' ? (flags['project'] as string).trim() : ''
  const cardId = typeof flags['card'] === 'string' ? (flags['card'] as string).trim() : ''

  try {
    const result = (await api.post(`/workspaces/${workspaceSlug}/topics`, {
      ...body,
      ...(projectId ? { project_id: projectId } : {}),
      ...(cardId ? { card_id: cardId } : {}),
    })) as { topic?: string; slug?: string; title?: string }
    const slug = result.topic ?? result.slug ?? body.name

    analyticsEvent('note_added', workspaceSlug, {
      has_project: !!projectId,
      has_card: !!cardId,
      suggest_topic: !!flags['suggest-topic'],
    })
    if (isJSON) {
      emitJSON({
        slug,
        title: result.title ?? slug,
        url: `${DASHBOARD_URL}/w/${workspaceSlug}/topic/${slug}`,
      })
    } else {
      console.log(`Note saved. Send to Inbox when ready → driftless context propose ${slug}`)
    }
    await flushAndExit(0)
  } catch (e: any) {
    console.error(`Could not save note: ${formatError(e)}`)
    await flushAndExit(1)
  }
}
